import { FilePondFile } from "filepond";
import { FGCustomPanel, FGListen, FGMaskInput, FGSelectInput, FGTextInput } from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import * as Yup from "yup";

import {
  CharacteristicApi,
  DogHealthTestApi,
  DogsTransientCharacteristicsApi,
  EHealthValueType,
} from "../../../../api";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useSaveApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";

export interface IDrupalDogHealthTestPanelProps {
  dogId: number;
  onSaved: () => void;
}

export const DrupalDogHealthTestPanel: React.FunctionComponent<IDrupalDogHealthTestPanelProps> = ({
  dogId,
  onSaved,
}) => {
  const { t } = useTl();
  const api = useApiService(DogHealthTestApi);
  const transientApi = useApiService(DogsTransientCharacteristicsApi);
  const characteristicApi = useApiService(CharacteristicApi);

  const [valueRequest, setValueRequest] = React.useState<number>();
  const [healthValueTypeCode, setHealthValueTypeCode] = React.useState<EHealthValueType>();
  const [characteristicFile, setCharacteristicFile] = React.useState<FilePondFile>(null);

  const [values, vloading] = useReferential((a) => (valueRequest ? a.getHealthValues(valueRequest) : null), false, [
    valueRequest,
  ]);
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      characteristicId: Yup.number().nullable().required(t(ETLCodes.Required)),
      numericValue: Yup.number()
        .nullable()
        .test("required", t(ETLCodes.Required), function (value) {
          if (this.parent.alphaValue === null && this.parent.healthValueId === null) {
            return value !== null;
          }
          return true;
        }),
      alphaValue: Yup.number()
        .nullable()
        .test("required", t(ETLCodes.Required), function (value) {
          if (this.parent.numericValue === null && this.parent.healthValueId === null) {
            return value !== null;
          }
          return true;
        }),
      healthValueId: Yup.number()
        .nullable()
        .test("required", t(ETLCodes.Required), function (value) {
          if (this.parent.alphaValue === null && this.parent.numericValue === null) {
            return value !== null;
          }
          return true;
        }),
    });
  }, [t]);
  const { currentLanguage } = useGlobalData();

  const [characteristics, cloading] = useReferential(
    (a) => a.getCharacteristics(false, currentLanguage.languageId),
    false,
    []
  );

  const { saving, saveItem } = useSaveApi<any>(
    React.useMemo(
      () => ({
        saveApiFn: (data) =>
          transientApi.create(
            dogId,
            data.characteristicId,
            data.healthValueId,
            data.numericValue,
            data.alphaValue,
            characteristicFile ? { data: characteristicFile.file, fileName: characteristicFile.filename } : null
          ),
        onSaveFn: () => onSaved(),
      }),
      [characteristicFile, dogId, onSaved, transientApi]
    )
  );
  const initialData = React.useMemo(() => {
    return { dogId: dogId };
  }, [dogId]);

  return (
    <DrupalFormGenerator
      onSubmit={saveItem}
      saving={saving}
      initialValues={initialData}
      editMode={true}
      validationSchema={FormSchema}
      minLabelWidth={110}
    >
      <FGListen
        field="characteristicId"
        onChanged={async (value) => {
          setValueRequest(value);
          setHealthValueTypeCode(value ? await characteristicApi.getHealthValueTypeCode(value) : null);
        }}
      />
      <FGSelectInput
        name="characteristicId"
        items={characteristics}
        loading={cloading}
        label={t(ETLCodes.Characteristic)}
      />

      {healthValueTypeCode === EHealthValueType.ValueList ? (
        <FGSelectInput name="healthValueId" items={values} loading={vloading} label={t(ETLCodes.Value)} />
      ) : healthValueTypeCode === EHealthValueType.Numeric ? (
        <FGMaskInput name="numericValue" label={t(ETLCodes.Value)} cleaveOptions={{ numericOnly: true }} />
      ) : (
        <FGTextInput name="alphaValue" label={t(ETLCodes.Value)} />
      )}
      <FGCustomPanel>
        {(ctx) => (
          <FilePond
            labelIdle={t(ETLCodes.FilePondDragOrSelect)}
            onupdatefiles={(f) => {
              ctx.formik.setFieldValue("characteristicFile", f.length > 0 ? f[0] : null);
              setCharacteristicFile(f.length > 0 ? f[0] : null);
            }}
          />
        )}
      </FGCustomPanel>
    </DrupalFormGenerator>
  );
};
